<template>
    <v-app>
        <v-container class="header" v-if="showHeaderFooter">
            <header role="banner">
                <v-row no-gutters justify="space-between">
                    <v-col cols="auto">
                        <router-link to="/">
                            <img :height="70" :src="require('@/assets/SCB_WebLogo.png')" alt="Standards Coordinating Body | Regenerative Medicine - Standards Portal" />
                        </router-link>
                    </v-col>
                    <v-col class="text-right">
                        <h1 class="display-1">The Regenerative Medicine Standards Portal</h1>
                        <nav class="mt-1" role="navigation">
                            <v-btn text color="primary" @click.stop="allStandards">Search for Standards</v-btn>
                            <v-btn text color="primary" to="/about">About</v-btn>
                            <v-btn text color="primary" to="/contact">Contact</v-btn>
                            <span class="mx-1">|</span>
                            <v-btn text color="primary" href="https://www.standardscoordinatingbody.org/" target="_blank" v-track>SCB Home</v-btn>
                        </nav>
                    </v-col>
                </v-row>
            </header>
        </v-container>
        <v-container fluid class="banner pa-0 mb-3" v-if="showHeaderFooter">
            <div role="region">
                <v-img :src="require('@/assets/banner.png')" :height="bannerImageHeight"></v-img>
                <div class="intro d-flex align-center" :style="cssVars" v-if="showIntro">
                    <v-container class="pa-0">
                        <div class="intro-content d-flex justify-center" ref="introContent">
                            <div class="intro-content-wrapper">
                                <div class="intro-title">A unique interactive database of the landscape of regenerative medicine standards</div>
                                <div>Search for standards and areas of need, provide feedback, and discover ways to contribute to and engage with the standards development process</div>
                            </div>
                        </div>
                    </v-container>
                </div>
            </div>
        </v-container>
        <v-content class="mb-6">
            <router-view></router-view>
        </v-content>
        <v-footer padless v-if="showHeaderFooter">
            <v-container fluid class="ma-0 pa-0 footer">
                <div class="text-center my-5">
                    <v-row justify="center">
                        <v-col cols="auto" class="text-left mx-12">
                            <div class="white--text mb-1">Data compiled by:</div>
                            <a href="https://www.standardscoordinatingbody.org/" target="_blank" v-track>
                                <img :height="70" :src="require('@/assets/SCB_Logo_White.png')" alt="Standards Coordinating Body | Regenerative Medicine" />
                            </a>
                        </v-col>
                        <v-col cols="auto" class="text-left mx-12">
                            <div class="white--text mb-3">Designed by:</div>
                            <a href="https://www.nexightgroup.com/" target="_blank" v-track>
                                <img :height="20" :src="require('@/assets/NG_logo_White-Orange_small.png')" alt="Nexight Group" />
                            </a>
                        </v-col>
                    </v-row>
                </div>
                <div class="text-center my-5">
                    <v-btn text to="/">Home</v-btn>
                    <span class="mx-0">|</span>
                    <v-btn text to="/search">Search for Standards</v-btn>
                    <span class="mx-0">|</span>
                    <v-btn text to="/about">About</v-btn>
                    <span class="mx-0">|</span>
                    <v-btn text to="/contact">Contact</v-btn>
                </div>
                <div class="text-center my-5">Copyright &copy; {{ Date.now() | date('YYYY') }}</div>
            </v-container>
        </v-footer>
        <ContactDialog ref="contactDialog"></ContactDialog>
        <DefinitionsDialog ref="definitionsDialog"></DefinitionsDialog>
    </v-app>
</template>

<script>
    import ContactDialog from '@/components/ContactDialog';
    import DefinitionsDialog from '@/components/DefinitionsDialog';
    import { throttle } from 'lodash';

    export default {
        name: 'App',
        components: { ContactDialog, DefinitionsDialog },
        data: () => ({
            introContentHeight: 0,
            observer: null
        }),
        computed: {
            showHeaderFooter() {
                return this.$route?.name && !/^admin/.test(this.$route.name);
            },
            showIntro() {
                return this.$route?.name && 'home' === this.$route.name;
            },
            bannerImageHeight() {
                return Math.max(this.introHeight, 160);
            },
            introHeight() {
                return this.introContentHeight + 60;
            },
            cssVars() {
                return {
                    '--intro-height': this.introHeight + 'px'
                };
            }
        },
        watch: {
            $route() {
                this.getIntroContentHeight();
            }
        },
        mounted() {
            this.$root.$contactDialog = this.$refs.contactDialog;
            this.$root.$definitionsDialog = this.$refs.definitionsDialog;

            this.getIntroContentHeight();
            this.observer = new ResizeObserver(this.getIntroContentHeight);
            this.observer.observe(this.$el);
        },
        destroyed() {
            this.observer.disconnect();
        },
        methods: {
            allStandards() {
                this.$store.commit('resetFilters', true);
                this.$store.dispatch('search').then(() => {
                    const searchPath = this.$store.get('searchPath');
                    if (this.$router.currentRoute?.path !== searchPath) {
                        this.$router.push({ path: searchPath });
                    }
                });
            },
            getIntroContentHeight: throttle(function () {
                if (this.showIntro) {
                    this.$nextTick(() => {
                        if (this.$refs.introContent) {
                            this.introContentHeight = this.$refs.introContent.clientHeight;
                        }
                    });
                } else {
                    this.introContentHeight = 0;
                }
            }, 250)
        }
    };
</script>

<style lang="scss">
    .v-btn {
        font-family: $heading-font-family;
    }

    .banner {
        position: relative;
    }
    .intro {
        position: absolute;
        top: 0;
        width: 100%;
        height: var(--intro-height);
        .intro-content {
            color: #fff;
            background-color: rgba(128, 0, 128, 0.5);
            border: 1px solid #fff;
            .intro-content-wrapper {
                padding: 15px 30px;
                .intro-title {
                    font-family: $heading-font-family;
                    font-weight: bold;
                    font-size: 24px;
                }
            }
        }
    }

    .header,
    .footer {
        .theme--light.v-btn::before,
        .theme--light.v-btn--active::before {
            opacity: 0 !important;
        }
        .theme--light.v-btn:hover,
        .theme--light.v-btn--active {
            font-weight: bold;
        }
    }

    .footer {
        color: rgba(255, 255, 255, 0.5);
        background-color: #1f1f1f;
        .v-btn {
            color: rgba(255, 255, 255, 0.5) !important;
        }
    }
</style>
